import { useState } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { useLingui } from '@lingui/react/macro'

import Button from './Button'
import { dialogContainer } from '@/layouts/Dialog'
import Input from './Input'
import Field from './Field'

type Props = React.PropsWithChildren<{
  title: string
  message: string
  cancel?: string
  confirm?: string
  onConfirm: () => void
  onCancel?: () => void
  asChild?: boolean
  isOpen?: boolean
  needsConfirmInput?: boolean
  confirmInputText?: string
}>

export default function ConfirmDialog({
  children,
  title,
  message,
  cancel,
  confirm,
  onConfirm,
  onCancel = () => null,
  asChild,
  isOpen = false,
  needsConfirmInput = false,
  confirmInputText
}: Props) {
  const { t } = useLingui()

  const [open, setOpen] = useState(isOpen)
  const [confirmValue, setConfirmValue] = useState('')

  const cancelLabel = cancel ?? t`Cancel`
  const confirmLabel = confirm ?? t`Confirm`

  const disableConfirm = needsConfirmInput && confirmInputText !== confirmValue

  return (
    // We stop propagation here, so that the Confirm Dialog buttons never propagate above itself
    // This is especially helpful in Tables where the whole Row is clickable
    <div onClick={event => event.stopPropagation()}>
      <AlertDialog.Root open={open} onOpenChange={setOpen}>
        <AlertDialog.Trigger asChild={asChild}>{children}</AlertDialog.Trigger>
        <AlertDialog.Portal container={dialogContainer}>
          <AlertDialog.Overlay className="fixed w-screen h-screen inset-0 bg-black/30 grid place-items-center overflow-y-auto z-top-dialog">
            <AlertDialog.Content className="relative w-screen max-w-[500px] py-10 px-2">
              <div className="shadow-md rounded bg-white flex flex-col gap-4 p-5 size-fit">
                <AlertDialog.Title className="text-heading2">
                  {title}
                </AlertDialog.Title>
                <AlertDialog.Description className="text-regular">
                  {message}
                </AlertDialog.Description>
                {needsConfirmInput && (
                  <Field label={t`Write "${confirmInputText}" to confirm.`}>
                    <Input
                      type="text"
                      value={confirmValue}
                      onChange={event => setConfirmValue(event.target.value)}
                    />
                  </Field>
                )}
                <div className="flex flex-row justify-end gap-4">
                  <AlertDialog.Cancel asChild>
                    <Button variant="outline" onClick={onCancel}>
                      {cancelLabel}
                    </Button>
                  </AlertDialog.Cancel>
                  <AlertDialog.Action asChild>
                    <Button onClick={onConfirm} disabled={disableConfirm}>
                      {confirmLabel}
                    </Button>
                  </AlertDialog.Action>
                </div>
              </div>
            </AlertDialog.Content>
          </AlertDialog.Overlay>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  )
}
